import {FormField} from '@components/system/FormField/FormField';
import {
  PROCEDURE_FIELD_OPTION_SOURCE,
  PROCEDURE_FIELD_TYPE
} from '@shared/constants/Mapping';
import {IProcedureInputField} from '@shared/services/service-mappers/SmartCoderServiceMapper';
import React from 'react';
import {Controller, UseFormMethods} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import get from 'lodash/get';

interface IProcedureInputFieldsProps {
  fields: IProcedureInputField[];
  clearFieldValue: (key: string) => void;
  procedureId: number;
  useFormMethods: UseFormMethods;
  defaultValues: Record<string, any>;
  onInputValueChange?: (formKey?: string) => void;
  fieldNamePrefix?: string;
  procedureName?: string;
}

const ProcedureInputFields = ({
  fields,
  clearFieldValue,
  procedureId,
  useFormMethods,
  defaultValues,
  onInputValueChange,
  fieldNamePrefix,
  procedureName = ''
}: IProcedureInputFieldsProps): JSX.Element => {
  const {t} = useTranslation();

  const procedureInputFields = fields.map(f => {
    const inputFieldProps: Record<string, any> = {};
    inputFieldProps.name = f.field_name_id;
    if (fieldNamePrefix) {
      inputFieldProps.name = `${fieldNamePrefix}.${f.field_name_id}`;
    }
    inputFieldProps.label = f.field_title;
    inputFieldProps.type = f.field_type;
    inputFieldProps.placeholder = f.field_placeholder;
    inputFieldProps.required = f.field_required;
    inputFieldProps.defaultValue = f.field_default_value; // DON'T PASS IT TO INPUT FIELDS
    inputFieldProps.fieldStyleOptions = f.field_style_options;

    const onClearFieldValue = () => {
      clearFieldValue(inputFieldProps.name);
    };

    if (f.field_type === PROCEDURE_FIELD_TYPE.SELECT) {
      if (f.field_multiple_select) {
        inputFieldProps.multiple = true;
        if (f.field_max_select) {
          inputFieldProps.maxSelection = f.field_max_select;
        }
      }
      if (f.field_options_source === PROCEDURE_FIELD_OPTION_SOURCE.STATIC) {
        inputFieldProps.itemList = Array.isArray(f.field_options)
          ? f.field_options
          : [];
      }
      if (!f.field_required) {
        inputFieldProps.onClear = onClearFieldValue;
      }
    } else if (f.field_type === PROCEDURE_FIELD_TYPE.RADIO) {
      if (f.field_options_source === PROCEDURE_FIELD_OPTION_SOURCE.STATIC) {
        inputFieldProps.options = Array.isArray(f.field_options)
          ? f.field_options
          : [];
      }
      if (!f.field_required) {
        inputFieldProps.onClear = onClearFieldValue;
      }
      // right now only radio field supports displaying helper text
      // if other fields need to display helper text, move this logic to the top
      if (f.field_helper_text) {
        inputFieldProps.fieldInfoText = (
          <div dangerouslySetInnerHTML={{__html: f.field_helper_text}} />
        );
      }
    } else if (f.field_type === PROCEDURE_FIELD_TYPE.CHECKBOX) {
      if (f.field_multiple_select) {
        inputFieldProps.multiple = true;
        if (f.field_max_select) {
          inputFieldProps.maxSelection = f.field_max_select;
        }
      }
      if (f.field_options_source === PROCEDURE_FIELD_OPTION_SOURCE.STATIC) {
        inputFieldProps.options = Array.isArray(f.field_options)
          ? f.field_options
          : [];
      }
    }
    return inputFieldProps;
  });

  return (
    <React.Fragment>
      {procedureInputFields.map(({defaultValue = null, ...inputItem}) => {
        const customInputFieldProps: Record<string, any> = {};
        if (inputItem.type === PROCEDURE_FIELD_TYPE.SELECT) {
          customInputFieldProps.wrapOptionLabel = true;
        }
        return (
          <Controller
            key={inputItem.name}
            name={inputItem.name}
            control={useFormMethods.control}
            defaultValue={get(defaultValues, inputItem.name) ?? defaultValue}
            rules={{
              validate: value => {
                if (inputItem.required) {
                  if (!value) {
                    return t(
                      'SMART_CODER.GENERATE_CPT_CODES_DIALOG.VALIDATION_ERROR_FIELD_REQUIRED',
                      {
                        field: inputItem.label,
                        procedure_name: procedureName
                      }
                    ) as string;
                  } else if (
                    (inputItem.type === PROCEDURE_FIELD_TYPE.SELECT ||
                      inputItem.type === PROCEDURE_FIELD_TYPE.CHECKBOX) &&
                    inputItem.multiple
                  ) {
                    if (Array.isArray(!value)) {
                      return t(
                        'SMART_CODER.GENERATE_CPT_CODES_DIALOG.VALIDATION_ERROR_FIELD_REQUIRED',
                        {
                          field: inputItem.label,
                          procedure_name: procedureName
                        }
                      ) as string;
                    }
                    if (
                      inputItem.maxSelection &&
                      value.length > inputItem.maxSelection
                    ) {
                      return t(
                        'SMART_CODER.GENERATE_CPT_CODES_DIALOG.VALIDATION_ERROR_MAX_SELECTION',
                        {
                          field: inputItem.label,
                          maxSelection: inputItem.maxSelection,
                          procedure_name: procedureName
                        }
                      ) as string;
                    }
                  }
                }
                return true;
              }
            }}
            render={inputProps => {
              return (
                <FormField
                  {...inputItem}
                  {...inputProps}
                  {...customInputFieldProps}
                  error={get(useFormMethods.errors, inputItem.name)}
                  onChange={(...args: any) => {
                    inputProps.onChange(...args);
                    if (onInputValueChange) {
                      onInputValueChange(inputItem.name);
                    }
                  }}
                />
              );
            }}
          />
        );
      })}
    </React.Fragment>
  );
};

export default ProcedureInputFields;
